import { useEffect } from "react";
import { useFinancialInsights } from "@/hooks/use-ai-features";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Sparkles, ChevronRight, TrendingUp, TrendingDown, AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

export function FinancialInsightsCard() {
  const { insights, isLoading, error, refetch } = useFinancialInsights();
  
  // Debug what data we're receiving
  console.log('AI Financial Insights - Raw Data:', insights);
  console.log('AI Financial Insights - Loading State:', isLoading);
  console.log('AI Financial Insights - Error State:', error);

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive" className="ml-2">High</Badge>;
      case 'medium':
        return <Badge variant="default" className="ml-2">Medium</Badge>;
      case 'low':
        return <Badge variant="outline" className="ml-2">Low</Badge>;
      default:
        return null;
    }
  };
  
  // Get toast function
  const { toast } = useToast();
  
  // Handle error toasts
  useEffect(() => {
    if (error) {
      toast({
        title: "Failed to load financial insights",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Analyzing your financial data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <AlertTriangle className="h-8 w-8 text-destructive mb-4" />
          <p className="text-destructive font-medium">Unable to generate insights</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {error instanceof Error ? error.message : "An unknown error occurred"}
          </p>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      );
    }

    if (!insights) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground mb-4">No financial insights available</p>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            Generate Insights
          </Button>
        </div>
      );
    }

    return (
      <Tabs defaultValue="insights">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="insights" className="text-xs sm:text-sm">Insights</TabsTrigger>
          <TabsTrigger value="recommendations" className="text-xs sm:text-sm">Tips</TabsTrigger>
          <TabsTrigger value="summary" className="text-xs sm:text-sm">Summary</TabsTrigger>
        </TabsList>
        
        <TabsContent value="insights" className="space-y-4 pt-4">
          {(insights as any)?.insights?.insights && Array.isArray((insights as any).insights.insights) ? (insights as any).insights.insights.map((insight: any, index: number) => (
            <div key={index} className="border rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <div className="flex flex-wrap justify-between items-start gap-2">
                <h4 className="font-medium text-sm sm:text-base">{insight.title}</h4>
                <div className="flex-shrink-0">
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="ml-2">
                        {insight.severity === 'warning' && (
                          <Badge variant="destructive" className="cursor-pointer hover:bg-red-600 transition-colors">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Warning
                          </Badge>
                        )}
                        {insight.severity === 'positive' && (
                          <Badge variant="default" className="bg-green-500 cursor-pointer hover:bg-green-600 transition-colors">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Positive
                          </Badge>
                        )}
                        {insight.severity === 'info' && (
                          <Badge variant="outline" className="cursor-pointer hover:bg-gray-100 transition-colors">
                            <Info className="w-3 h-3 mr-1" />
                            Info
                          </Badge>
                        )}
                      </div>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          {insight.severity === 'warning' && <AlertTriangle className="w-5 h-5 text-red-500" />}
                          {insight.severity === 'positive' && <TrendingUp className="w-5 h-5 text-green-500" />}
                          {insight.severity === 'info' && <Info className="w-5 h-5 text-blue-500" />}
                          {insight.title}
                        </DialogTitle>
                        <DialogDescription>
                          Detailed insight about your business finances
                        </DialogDescription>
                      </DialogHeader>
                      <div className="mt-4 space-y-4">
                        <div>
                          <h4 className="font-medium text-sm mb-2">Description</h4>
                          <p className="text-sm text-muted-foreground">{insight.description}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sm mb-2">Insight Type</h4>
                          <p className="text-sm text-muted-foreground capitalize">{insight.type?.replace(/_/g, ' ')}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sm mb-2">Priority Level</h4>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={insight.severity === 'warning' ? 'destructive' : insight.severity === 'positive' ? 'default' : 'outline'}
                              className={insight.severity === 'positive' ? 'bg-green-500' : ''}
                            >
                              {insight.severity === 'warning' ? 'High Priority' : insight.severity === 'positive' ? 'Good News' : 'Information'}
                            </Badge>
                          </div>
                        </div>
                        {insight.severity === 'warning' && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-700">
                              <strong>Action Required:</strong> This insight indicates something that may need your attention to maintain optimal business performance.
                            </p>
                          </div>
                        )}
                        {insight.severity === 'positive' && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-sm text-green-700">
                              <strong>Great News:</strong> This insight highlights positive aspects of your business performance. Keep up the good work!
                            </p>
                          </div>
                        )}
                        {insight.severity === 'info' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700">
                              <strong>Information:</strong> This insight provides helpful context about your business activity and trends.
                            </p>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-2">{insight.description}</p>
            </div>
          )) : (
            <div className="text-center py-4 text-muted-foreground">
              No insights available
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="recommendations" className="space-y-4 pt-4">
          {(insights as any)?.insights?.recommendations?.map((rec: any, index: number) => (
            <div key={index} className="border rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <div className="flex items-start">
                <div className="mr-3 mt-1">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">{rec}</p>
              </div>
            </div>
          )) || (
            <div className="text-center py-4 text-muted-foreground">
              No recommendations available
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="summary" className="pt-4">
          {(insights as any)?.insights?.summary ? (
            <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {Object.entries((insights as any).insights.summary).map(([key, value]: [string, any], i: number) => (
                <div key={i} className="border rounded-lg p-2 sm:p-3">
                  <p className="text-2xs sm:text-xs text-muted-foreground uppercase tracking-wide">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</p>
                  <p className="text-base sm:text-lg font-semibold mt-1">
                    {key.includes('Income') || key.includes('Expenses') || key.includes('Profit') ? `$${value}` : 
                     key.includes('Margin') ? `${value}%` : value}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              No summary available
            </div>
          )}
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2 sm:pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-1 sm:mr-2" />
            <CardTitle className="text-base sm:text-lg">AI Financial Insights</CardTitle>
          </div>
          <Button onClick={() => refetch()} variant="ghost" size="sm" className="h-8 w-8 p-0">
            <span className="sr-only">Refresh insights</span>
            <Loader2 className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <CardDescription className="text-xs sm:text-sm">
          AI-powered analysis of your business finances
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 sm:pt-2">
        {renderContent()}
      </CardContent>
    </Card>
  );
}